import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext
} from "react";

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  where
} from "firebase/firestore";

import { db } from "../utils/firebase";
import { useAuth } from "./AuthContext";
import { TokenContext } from "./tokenContext";
import { logout } from "../utils/firebase";
import { createPortal } from "react-dom";
import GlobalNavDrawer from "./GlobalNavDrawer";
import { redirectToJobBankLogin } from "./jobBankRole";

import "./JobCarveOutPortal.css";

function cleanList(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  return String(value || "")
    .split("\n")
    .map(item => item.trim())
    .filter(Boolean);
}

function formatMoney(value) {
  const number = Number(value || 0);

  if (!number) {
    return "Wage not set";
  }

  return `$${number.toFixed(2)}/hr`;
}

export default function JobCarveOutPortal() {
  const { currentUser } = useAuth();
  const { balance, setBalance } = useContext(TokenContext);
  const [carveOuts, setCarveOuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryText, setQueryText] = useState("");
  const [requestingId, setRequestingId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchTokens() {
      if (!currentUser?.uid) return;
      try {
        const userSnap = await getDoc(doc(db, "users", currentUser.uid));
        if (userSnap.exists() && isMounted) {
          setBalance(userSnap.data().tokens ?? 0);
        }
      } catch (error) {
        console.error("Error fetching token count:", error);
      }
    }

    fetchTokens();

    return () => {
      isMounted = false;
    };
  }, [currentUser, setBalance]);

  const loadCarveOuts = useCallback(async () => {
    try {
      setLoading(true);

      const carveOutQuery = query(
        collection(db, "jobCarveOuts"),
        where("status", "==", "Open"),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(carveOutQuery);

      setCarveOuts(
        snapshot.docs.map(carveOutDoc => ({
          id: carveOutDoc.id,
          ...carveOutDoc.data()
        }))
      );
    } catch (error) {
      console.error("Unable to load job carve-outs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCarveOuts();
  }, [loadCarveOuts]);

  const filteredCarveOuts = useMemo(() => {
    const needle = queryText.trim().toLowerCase();

    if (!needle) {
      return carveOuts;
    }

    return carveOuts.filter(carveOut => {
      const searchable = [
        carveOut.title,
        carveOut.companyName,
        carveOut.description,
        carveOut.idealCandidateProfile,
        carveOut.schedule,
        ...(cleanList(carveOut.barriersSupported)),
        ...(cleanList(carveOut.taskExamples))
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(needle);
    });
  }, [carveOuts, queryText]);

  async function requestCarveOut(carveOut) {
    if (!currentUser) {
      redirectToJobBankLogin("/jobcarveouts");
      return;
    }

    try {
      setRequestingId(carveOut.id);

      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists()
        ? userSnap.data()
        : {};

      await addDoc(
        collection(db, "jobCarveOutRequests"),
        {
          carveOutId: carveOut.id,
          carveOutTitle: carveOut.title || "Job Carve-Out",
          companyId: carveOut.companyId || "",
          companyName: carveOut.companyName || "",
          requestedByUid: currentUser.uid,
          requestedByEmail: userData.email || currentUser.email || "",
          requestedByName: [
            userData.firstName,
            userData.lastName
          ].filter(Boolean).join(" ") || currentUser.displayName || "",
          requesterType: "Candidate",
          status: "Requested",
          notes: userData.carveOutNotes || "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
      );

      alert("Your job carve-out request has been submitted.");
    } catch (error) {
      console.error("Unable to request job carve-out:", error);
      alert("Unable to request this job carve-out.");
    } finally {
      setRequestingId(null);
    }
  }

  return (
    <div className="jobCarveOutPortal">
      <header className="carveOutHero">
        <p className="carveOutEyebrow">
          The iSpeakWell Project Job Bank
        </p>

        <h1>Job Carve-Outs</h1>

        <p>
          Explore customized employment opportunities designed around
          strengths, barriers, hours, tasks, supervision needs, and real
          business value.
        </p>
      </header>

      <div className="carveOutSearchPanel">
        <input
          value={queryText}
          placeholder="Search carve-outs, barriers supported, employers, or tasks..."
          onChange={(event) => setQueryText(event.target.value)}
        />

        <button onClick={loadCarveOuts}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="carveOutEmpty">
          Loading job carve-outs...
        </div>
      ) : filteredCarveOuts.length === 0 ? (
        <div className="carveOutEmpty">
          No open job carve-outs found yet.
        </div>
      ) : (
        <div className="carveOutGrid">
          {filteredCarveOuts.map(carveOut => (
            <article
              className="carveOutCard"
              key={carveOut.id}
            >
              <div className="carveOutCardHeader">
                <div>
                  <h2>{carveOut.title || "Job Carve-Out"}</h2>
                  <p>{carveOut.companyName || "Employer"}</p>
                </div>

                <span>{carveOut.status || "Open"}</span>
              </div>

              <p className="carveOutDescription">
                {carveOut.description || "No description provided yet."}
              </p>

              <div className="carveOutMetaGrid">
                <div>
                  <strong>Hours</strong>
                  <p>{carveOut.hoursPerWeek || "Flexible"} hrs/week</p>
                </div>

                <div>
                  <strong>Schedule</strong>
                  <p>{carveOut.schedule || "Flexible"}</p>
                </div>

                <div>
                  <strong>Supervision</strong>
                  <p>{carveOut.supervisionLevel || "Not set"}</p>
                </div>

                <div>
                  <strong>Wage</strong>
                  <p>{formatMoney(carveOut.wage)}</p>
                </div>
              </div>

              <section>
                <h3>Example Tasks</h3>
                <ul>
                  {cleanList(carveOut.taskExamples).length === 0 ? (
                    <li>No task examples listed.</li>
                  ) : cleanList(carveOut.taskExamples).map(task => (
                    <li key={task}>{task}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h3>Barriers Supported</h3>
                <div className="carveOutPills">
                  {cleanList(carveOut.barriersSupported).length === 0 ? (
                    <span>Flexible support</span>
                  ) : cleanList(carveOut.barriersSupported).map(barrier => (
                    <span key={barrier}>{barrier}</span>
                  ))}
                </div>
              </section>

              <button
                className="requestCarveOutButton"
                onClick={() => requestCarveOut(carveOut)}
                disabled={requestingId === carveOut.id}
              >
                {requestingId === carveOut.id
                  ? "Requesting..."
                  : "Request This Carve-Out"}
              </button>
            </article>
          ))}
        </div>
      )}
      {createPortal(
        <GlobalNavDrawer
          hideCurrent
          links={[
            { to: "/purchase", label: `Tokens: ${balance}` },
            { to: "/jobbank", label: "Job Bank Home" },
            { to: "/jobbankjobs", label: "Jobs" },
            { to: "/careercoaches", label: "Career Coaches" },
            { to: "/events", label: "Hiring Events" },
            { to: "/profile", label: "My Profile" },
            { to: "/voicebot", label: "iSpeakWell" },
            { to: "/resumerevisor", label: "Resumes" },
            { to: "/admin", label: "Admin" },
            { to: "/login", label: "Logout", onClick: async () => { await logout(); } }
          ]}
        />,
        document.body
      )}
    </div>
  );
}
